import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import ImageUpload from '../../components/ImageUpload';
import BulkImageUpload from '../../components/BulkImageUpload';
import { Plus, Edit2, Trash2, X, Save, Upload, Filter } from 'lucide-react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGallery = () => {
  const { getAuthHeader } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [formData, setFormData] = useState({
    title: '',
    category: 'Health',
    image: '',
    date: new Date().toISOString().split('T')[0]
  });

  const categories = ['Health', 'Education', 'Infrastructure', 'Community', 'Governance', 'Economic Development'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gallery`);
      setItems(response.data);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/gallery/${editingId}`, formData, { headers: getAuthHeader() });
        toast({ title: 'Success', description: 'Image updated' });
      } else {
        await axios.post(`${API_URL}/api/gallery`, formData, { headers: getAuthHeader() });
        toast({ title: 'Success', description: 'Image added' });
      }
      resetForm();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      category: item.category,
      image: item.image,
      date: item.date
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await axios.delete(`${API_URL}/api/gallery/${id}`, { headers: getAuthHeader() });
      toast({ title: 'Success', description: 'Image deleted successfully' });
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to delete image. Please try again.',
        variant: 'destructive' 
      });
    }
  };

  const resetForm = () => {
    setFormData({ title: '', category: 'Health', image: '', date: new Date().toISOString().split('T')[0] });
    setEditingId(null);
    setShowForm(false);
  };

  const handleBulkUploadComplete = () => {
    setShowBulkUpload(false);
    fetchData();
    toast({ title: 'Success', description: 'Images uploaded successfully' });
  };

  const filteredItems = selectedCategory === 'All' 
    ? items 
    : items.filter(item => item.category === selectedCategory);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Gallery Management</h1>
          <div className="flex space-x-3">
            <button 
              onClick={() => setShowBulkUpload(true)} 
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center space-x-2"
            >
              <Upload className="w-5 h-5" /><span>Bulk Upload</span>
            </button>
            <button onClick={() => setShowForm(true)} className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center space-x-2">
              <Plus className="w-5 h-5" /><span>Add Image</span>
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex items-center space-x-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-700">Filter:</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('All')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === 'All'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({items.length})
              </button>
              {categories.map(cat => {
                const count = items.filter(i => i.category === cat).length;
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === cat
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {cat} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bulk Upload Modal */}
        {showBulkUpload && (
          <BulkImageUpload
            category={formData.category}
            onUploadComplete={handleBulkUploadComplete}
            onClose={() => setShowBulkUpload(false)}
          />
        )}

        {showForm && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">{editingId ? 'Edit Image' : 'Add Image'}</h2>
              <button onClick={resetForm}><X className="w-6 h-6" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-4 py-2 border rounded-lg">
                  {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <ImageUpload
                  value={formData.image}
                  onChange={(url) => setFormData({ ...formData, image: url })}
                  label="Gallery Image"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Date</label>
                <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div className="flex space-x-4">
                <button type="submit" className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center justify-center space-x-2">
                  <Save className="w-5 h-5" /><span>{editingId ? 'Update' : 'Create'}</span>
                </button>
                <button type="button" onClick={resetForm} className="px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg">Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {loading ? (
            <div className="col-span-full p-8 text-center">Loading...</div>
          ) : filteredItems.length === 0 ? (
            <div className="col-span-full p-8 text-center text-gray-500">
              {selectedCategory === 'All' ? 'No images yet' : `No images in ${selectedCategory} category`}
            </div>
          ) : (
            filteredItems.map((item) => (
              <div key={item.id} className="bg-white rounded-xl shadow-lg overflow-hidden group">
                <div className="relative h-48 overflow-hidden">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                  <div className="absolute top-2 right-2 flex space-x-2">
                    <button onClick={() => handleEdit(item)} className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100">
                      <Edit2 className="w-4 h-4 text-purple-600" />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-sm mb-2 line-clamp-2">{item.title}</h3>
                  <div className="flex justify-between items-center text-xs">
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">{item.category}</span>
                    <span className="text-gray-500">{item.date}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminGallery;